import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { IProductivityMetric, ProductivityMetricIcon, ProductivityMetricId } from '@interfaces/IProductivity';
import {
  ActivityIcon,
  Building2Icon,
  GaugeIcon,
  TrendDownIcon,
  TrendUpIcon,
  UsersIcon,
} from '@utils/icons';
import { formatNumber } from '@utils/formatNumber';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import { MockPill } from '@components/ui/MockPill/MockPill';
import { Card } from '../../../shared/Card/Card';
import styles from './MetricCard.module.css';

const ICONS: Record<ProductivityMetricIcon, ComponentType<{ size?: number }>> = {
  gauge: GaugeIcon,
  activity: ActivityIcon,
  users: UsersIcon,
  building: Building2Icon,
};

/** Cards whose top-right corner shows a static badge when there is no trend. */
const BADGED: Partial<Record<ProductivityMetricId, true>> = {
  'workers-on-site': true,
  'performance-extremes': true,
};

/** Cards that carry an ⓘ explaining WHAT they measure — copy at
 *  `productivity.metrics.<id>.info`. Adding a card is adding its key here. */
const EXPLAINED: Partial<Record<ProductivityMetricId, true>> = {
  'project-productivity': true,
  'workers-on-site': true,
  'location-utilization': true,
  'performance-extremes': true,
};

/** The localised sublabel for a card, composed from its id + figures — the
 *  backend ships numbers only (same pattern as the exception cards). */
function sublabelFor(metric: IProductivityMetric, t: TFunction): string | null {
  const figures = metric.figures;
  switch (metric.id) {
    case 'project-productivity': {
      if (!figures) return null;
      if (figures.actualWorkDays) return t('productivity.metrics.project-productivity.sublabel');
      return t(
        figures.earnedWorkDays
          ? 'productivity.metrics.project-productivity.noLabour'
          : 'productivity.metrics.project-productivity.noData',
      );
    }
    case 'workers-on-site':
      if (figures?.workers == null) return t('productivity.metrics.workers-on-site.noData');
      return figures.subcontractors != null
        ? t('productivity.metrics.workers-on-site.sublabel', { count: figures.subcontractors })
        : null;
    case 'location-utilization':
      return figures?.outsideAssignedHours != null
        ? t('productivity.metrics.location-utilization.sublabel', {
            hours: formatNumber(figures.outsideAssignedHours),
          })
        : t('productivity.metrics.location-utilization.noData');
    default:
      return null;
  }
}

interface IMetricCardProps {
  metric: IProductivityMetric;
  mock?: boolean;
}

export function MetricCard({ metric, mock = false }: IMetricCardProps) {
  const { t } = useTranslation();
  const Icon = ICONS[metric.icon];
  const TrendIcon = metric.trend === 'up' ? TrendUpIcon : TrendDownIcon;
  const label = t(`productivity.metrics.${metric.id}.label`);
  const sublabel = sublabelFor(metric, t);
  const info = EXPLAINED[metric.id] ? t(`productivity.metrics.${metric.id}.info`) : null;

  const labelRow = (
    <p className={styles.label}>
      {label}
      {info && (
        <InfoTip text={info} className={styles.infoTip} />
      )}
      {mock && <MockPill kind="badge" />}
    </p>
  );

  return (
    <Card as="article">
      <div className={styles.cardTop}>
        <span className={styles.iconBox} data-tone={metric.tone}>
          <Icon size={16} />
        </span>
        {metric.trend ? (
          <span className={styles.trend} data-direction={metric.trend}>
            <TrendIcon size={13} />
            {metric.trendValue && <span className={styles.trendValue}>{metric.trendValue}</span>}
            {t('productivity.trend.vsLastWeek')}
          </span>
        ) : BADGED[metric.id] ? (
          <span className={styles.badge}>{t(`productivity.metrics.${metric.id}.badge`)}</span>
        ) : null}
      </div>

      {metric.extremes ? (
        <div className={styles.extremesBody}>
          {labelRow}
          {metric.extremes.length === 0 ? (
            <p className={styles.sublabel}>
              {t(`productivity.metrics.${metric.id}.noData`)}
            </p>
          ) : (
            <ul className={styles.extremeList}>
              {metric.extremes.map((row) => (
                <li key={row.name} className={styles.extremeRow}>
                  <span className={styles.extremeName}>{row.name}</span>
                  <span className={styles.extremePill} data-rank={row.rank}>
                    <span className={styles.extremeRank}>{t(`productivity.extremes.${row.rank}`)}</span>
                    {row.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <p className={styles.value}>{metric.value}</p>
          {labelRow}
          {sublabel && <p className={styles.sublabel}>{sublabel}</p>}
        </>
      )}
    </Card>
  );
}